// src/components/ProjectForm.jsx
import React, { useState, useEffect } from "react";
import MapboxLocationPicker from "./MapboxLocationPicker";
import ImageUploader from "./ImageUploader";
import * as TagsService from "../services/azureTagsService";
import {
  getProjectImages,
  updateImage,
} from "../services/azureProjectImagesService";
import MediaTable from "./MediaTable";
import { getProjectMedia } from "../services/azureProjectMediaService";
import PartnerAssignField from "./PartnerAssignField";
import { RELATION_TYPES } from "../services/azureRelationsService";
import { useAuth } from "../hooks/useAuth";
import { canToggleProjectWebsite } from "../utils/permissions";

const ProjectForm = ({
  project,
  employees,
  onSave,
  onCancel,
  loading,
  isEditing = false,
  type = "project",
}) => {
  const { user } = useAuth();
  // Nur GL entscheidet, ob ein Projekt eine externe Projektwebsite erhält.
  const mayToggleWebsite = canToggleProjectWebsite(user);
  const [formData, setFormData] = useState({
    id: null,
    code: "",
    public: null,
    title: "",
    projektstart: "",
    projektende: "",
    baukosten: "",
    honorar: "",
    importance: "",
    websiteUrl: "",
    type: "project",
    beschreibung: "",
    adresse: "",
    location: { lat: null, lng: null },
    auftraggeberin: "",
    tags: [],
    image: null,
    color: "#059669",
    comingSoon: false, // <-- Hinzufügen
    projektwebsite: false,
  });

  const [images, setImages] = useState({
    allgemein: [],
    plaene: [],
    prozess: [],
    mainPicture: [],
  });

  const [newTag, setNewTag] = useState("");
  const [errors, setErrors] = useState({});
  const [availableTags, setAvailableTags] = useState([]);
  const [popularTags, setPopularTags] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [media, setMedia] = useState([]);
  const [personSearch, setPersonSearch] = useState("");
  const [showPersonSuggestions, setShowPersonSuggestions] = useState(false);
  const [availablePersons, setAvailablePersons] = useState([]);

  useEffect(() => {
    const loadTags = async () => {
      try {
        const [allTags, popularTagsData] = await Promise.all([
          TagsService.getAllTags(),
          TagsService.getPopularTags(15),
        ]);
        setAvailableTags(allTags);
        setPopularTags(popularTagsData);
      } catch (error) {
        console.error("Fehler beim Laden der Tags:", error);
      }
    };
    loadTags();
  }, []);

  useEffect(() => {
    const loadPersons = async () => {
      try {
        const PersonsService = await import("../services/azurePersonsService");
        const personsData = await PersonsService.getAllPersons();
        setAvailablePersons(personsData);
      } catch (error) {
        console.error("Fehler beim Laden der Personen:", error);
      }
    };

    loadPersons();
  }, []);

  const loadProjectImages = async (projectId) => {
    try {
      const allImages = await getProjectImages(projectId);
      setImages({
        allgemein: allImages.filter((img) => img.kategorie === "allgemein"),
        plaene: allImages.filter((img) => img.kategorie === "plaene"),
        prozess: allImages.filter((img) => img.kategorie === "prozess"),
        mainPicture: allImages.filter(
          (img) => img.kategorie === "main-picture"
        ),
        coverImage: allImages.filter((img) => img.kategorie === "cover-image"),
      });
      const loadedMedia = await getProjectMedia(projectId);
      setMedia(loadedMedia);
    } catch (error) {
      console.error("Load images failed:", error);
    }
  };

  const loadProjectPersons = async (projectId) => {
    try {
      const RelationsService = await import(
        "../services/azureRelationsService"
      );
      const PersonsService = await import("../services/azurePersonsService");

      const relations = await RelationsService.getProjectRelations(projectId);
      const personRelations = relations.filter(
        (rel) => rel.targetType === "person"
      );

      const personsWithPhases = [];
      for (const relation of personRelations) {
        const person = await PersonsService.getPerson(relation.targetId);
        if (person) {
          const phases = relation.function ? relation.function.split(",") : [];
          personsWithPhases.push({ ...person, phases });
        }
      }

      setFormData((prev) => ({
        ...prev,
        persons: personsWithPhases,
      }));
    } catch (error) {
      console.error("Fehler beim Laden der Projekt-Personen:", error);
    }
  };

  useEffect(() => {
    if (project) {
      setFormData({
        id: project.id || null,
        code: project.code || "",
        public: project.public,
        comingSoon: project.comingSoon || false,
        // Muss hier mitgeladen werden: updateProject schreibt die Entity als
        // vollständiges PUT — ein fehlendes Feld würde den Schalter löschen.
        projektwebsite: project.projektwebsite === true,
        title: project.title || "",
        longtitle: project.longtitle || "",
        projektstart: project.projektstart || "",
        projektende: project.projektende || "",
        baukosten: project.baukosten?.toString() || "",
        honorar: project.honorar?.toString() || "",
        importance: project.importance || "",
        pic: project.pic || "",
        scale: project?.scale || "",
        websiteUrl: project.websiteUrl || "",
        type: project.type || "",
        beschreibung: project.beschreibung || "",
        adresse: project.adresse || "",
        location: project.location || { lat: null, lng: null },
        auftraggeberin: project.auftraggeberin || "",
        tags: project.tags || [],
        image: project.image || null,
        color: project.color || "#059669",
      });

      // Bilder und Personen laden
      if (project.id) {
        loadProjectImages(project.id);
        loadProjectPersons(project.id);
      }
    }
  }, [project]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleLocationChange = (newLocation, gemeinde) => {
    setFormData((prev) => ({
      ...prev,
      location: newLocation,
      ...(gemeinde && { adresse: gemeinde }),
    }));

    if (errors.location) {
      setErrors((prev) => ({
        ...prev,
        location: "",
      }));
    }
  };

  const handleImagesChange = (imageType, newImages) => {
    setImages((prev) => ({
      ...prev,
      [imageType]: newImages,
    }));
  };

  const getFilteredTags = () => {
    if (!newTag) return [];
    return availableTags
      .filter(
        (tag) =>
          tag.name.toLowerCase().includes(newTag.toLowerCase()) ||
          tag.category.toLowerCase().includes(newTag.toLowerCase())
      )
      .slice(0, 5);
  };

  const addTag = async () => {
    const tagName = newTag.trim();
    if (!tagName) return;

    try {
      if (
        formData.tags.some(
          (tag) => tag.name.toLowerCase() === tagName.toLowerCase()
        )
      ) {
        return;
      }

      let tag = availableTags.find(
        (t) => t.name.toLowerCase() === tagName.toLowerCase()
      );

      if (!tag) {
        tag = await TagsService.findOrCreateTag(tagName);
        setAvailableTags((prev) => [...prev, tag]);
      }

      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));

      setNewTag("");
      setShowTagSuggestions(false);
    } catch (error) {
      console.error("Fehler beim Hinzufügen des Tags:", error);
    }
  };

  const addPredefinedTag = async (tag) => {
    try {
      if (formData.tags.some((existingTag) => existingTag.id === tag.id)) {
        return;
      }

      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
    } catch (error) {
      console.error("Fehler beim Hinzufügen des Tags:", error);
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag.id !== tagToRemove.id),
    }));
  };

  const handleTagSelect = (tag) => {
    if (!formData.tags.some((t) => t.id === tag.id)) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
    }
    setNewTag("");
    setShowTagSuggestions(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (
      (formData.type === "project" || formData.type === "offerte") &&
      !formData.code
    ) {
      newErrors.code = "code ist erforderlich";
    }
    if (!formData.title) newErrors.title = "Titel ist erforderlich";

    if (formData.projektstart && formData.projektende) {
      if (new Date(formData.projektstart) > new Date(formData.projektende)) {
        newErrors.projektende = "Projektende muss nach Projektstart liegen";
      }
    }

    if (formData.baukosten && isNaN(parseFloat(formData.baukosten))) {
      newErrors.baukosten = "Bitte geben Sie eine gültige Zahl ein";
    }
    if (formData.honorar && isNaN(parseFloat(formData.honorar))) {
      newErrors.honorar = "Bitte geben Sie eine gültige Zahl ein";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFilteredPersons = () => {
    if (!personSearch) return [];
    return availablePersons
      .filter(
        (person) =>
          `${person.firstName} ${person.lastName}`
            .toLowerCase()
            .includes(personSearch.toLowerCase()) ||
          person.email?.toLowerCase().includes(personSearch.toLowerCase())
      )
      .filter((person) => !formData.persons?.some((p) => p.id === person.id))
      .slice(0, 5);
  };

  const handlePersonSelect = (person) => {
    setFormData((prev) => ({
      ...prev,
      persons: [...(prev.persons || []), { ...person, phases: [] }],
    }));
    setPersonSearch("");
    setShowPersonSuggestions(false);
  };

  const handlePersonPhasesChange = (personId, e) => {
    const selectedPhases = Array.from(
      e.target.selectedOptions,
      (option) => option.value
    );
    setFormData((prev) => ({
      ...prev,
      persons: prev.persons.map((person) =>
        person.id === personId ? { ...person, phases: selectedPhases } : person
      ),
    }));
  };

  const removePerson = (personId) => {
    setFormData((prev) => ({
      ...prev,
      persons: prev.persons.filter((p) => p.id !== personId),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Submit eines verschachtelten Formulars (CRM-Modale aus PartnerAssignField)
    // steigt im React-Baum bis hierher auf — nicht als Projekt-Speichern werten.
    if (e.target !== e.currentTarget) return;

    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      type: formData.type,
      baukosten: formData.baukosten ? parseFloat(formData.baukosten) : 0,
      honorar: formData.honorar ? parseFloat(formData.honorar) : 0,
    };

    try {
      const savedProject = await onSave(submitData);

      // Personen-Relations speichern
      const projectId = savedProject?.id || formData.id || project?.id;
      if (projectId && formData.persons && formData.persons.length > 0) {
        await saveProjectPersonRelations(projectId, formData.persons);
      } else if (projectId) {
        // Auch leere Personen speichern (löscht bestehende)
        await saveProjectPersonRelations(projectId, []);
      }

      // Bildmetadaten speichern (nur bei bestehendem Projekt)
      if (formData.id || (isEditing && project?.id)) {
        await saveImageMetadata();
      }
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  const saveProjectPersonRelations = async (projectId, persons) => {
    try {
      const RelationsService = await import(
        "../services/azureRelationsService"
      );

      // Bestehende Personen-Relations löschen
      const existingRelations = await RelationsService.getProjectRelations(
        projectId
      );
      const personRelations = existingRelations.filter(
        (rel) => rel.targetType === "person"
      );

      for (const relation of personRelations) {
        await RelationsService.deleteRelation(relation.id);
      }

      // Neue Relations erstellen
      for (const person of persons) {
        const phasesString = person.phases?.join(",") || "";
        await RelationsService.createRelation({
          sourceType: "project",
          sourceId: projectId,
          targetType: "person",
          targetId: person.id,
          relationType: RelationsService.RELATION_TYPES.PROJECT_PERSON,
          function: phasesString,
          description: `Person: ${person.firstName} ${person.lastName} - Phasen: ${phasesString}`,
        });
      }

      console.log(`${persons.length} Personen-Relations gespeichert`);
    } catch (error) {
      console.error("Fehler beim Speichern der Personen-Relations:", error);
      throw error;
    }
  };

  const saveImageMetadata = async () => {
    const allImages = [
      ...images.allgemein,
      ...images.plaene,
      ...images.prozess,
      ...images.mainPicture,
      ...images.coverImage,
    ];

    if (allImages.length === 0) {
      return;
    }

    console.log("Speichere Bildmetadaten für", allImages.length, "Bilder...");

    try {
      await Promise.all(
        allImages.map((img) =>
          updateImage(
            formData.id || project?.id,
            img.bildId,
            img.bildtext,
            img.isPublic
          )
        )
      );
      console.log("Alle Bildmetadaten gespeichert");
    } catch (error) {
      console.error("Fehler beim Speichern der Bildmetadaten:", error);
      alert(
        `Warnung: Bildmetadaten konnten nicht gespeichert werden: ${error.message}`
      );
    }
  };

  return (
    <div className="project-form-container">
      <form onSubmit={handleSubmit} className="project-form">
        <div className="form-section">
          <h3>Grunddaten</h3>
          <div className="form-group">
            <label htmlFor="type">Type</label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
            >
              <option value="project">Project</option>
              <option value="offerte">Offerte</option>
              <option value="research">Research</option>
              <option value="text">Text</option>
              <option value="exhibition">Exhibition</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="code">code *</label>
              <input
                type="text"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                className={errors.code ? "error" : ""}
                placeholder="z.B. 001"
              />
              {errors.code && (
                <span className="error-message">{errors.code}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="title">Titel *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={errors.title ? "error" : ""}
                placeholder="z.B. Gleispool"
              />
              {errors.title && (
                <span className="error-message">{errors.title}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="title">Longtitle</label>
              <input
                type="text"
                id="longtitle"
                name="longtitle"
                value={formData.longtitle}
                onChange={handleInputChange}
                className={errors.longtitle ? "error" : ""}
                placeholder="z.B. Gleispool Dreispitz"
              />
              {errors.title && (
                <span className="error-message">{errors.title}</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="projektstart">Projektstart</label>
              <input
                type="date"
                id="projektstart"
                name="projektstart"
                value={formData.projektstart}
                onChange={handleInputChange}
                className={errors.projektstart ? "error" : ""}
              />
              {errors.projektstart && (
                <span className="error-message">{errors.projektstart}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="projektende">Projektende</label>
              <input
                type="date"
                id="projektende"
                name="projektende"
                value={formData.projektende}
                onChange={handleInputChange}
                className={errors.projektende ? "error" : ""}
              />
              {errors.projektende && (
                <span className="error-message">{errors.projektende}</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="baukosten">Baukosten (CHF)</label>
              <input
                type="number"
                id="baukosten"
                name="baukosten"
                value={formData.baukosten}
                onChange={handleInputChange}
                className={errors.baukosten ? "error" : ""}
                placeholder="0.00"
                step="0.01"
              />
              {errors.baukosten && (
                <span className="error-message">{errors.baukosten}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="honorar">Honorar (CHF)</label>
              <input
                type="number"
                id="honorar"
                name="honorar"
                value={formData.honorar}
                onChange={handleInputChange}
                className={errors.honorar ? "error" : ""}
                placeholder="0.00"
                step="0.01"
              />
              {errors.honorar && (
                <span className="error-message">{errors.honorar}</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="pic">Projektleitung</label>
              <select
                id="pic"
                name="pic"
                value={formData.pic}
                onChange={handleInputChange}
              >
                <option value="">Bitte wählen...</option>
                {employees.map((emp) => (
                  <option key={emp.uuid} value={emp.uuid}>
                    {emp.firstName} {emp.lastName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              {isEditing && formData.code ? (
                <PartnerAssignField
                  projectCode={formData.code}
                  relationType={RELATION_TYPES.AUFTRAGGEBERIN}
                  label="Auftraggeberin"
                  placeholder="Firma suchen oder neu erfassen…"
                />
              ) : (
                <>
                  <label>Auftraggeberin</label>
                  <p className="field-hint">
                    Kann nach dem Erstellen des Projekts zugeordnet werden.
                  </p>
                </>
              )}
              {formData.auftraggeberin && (
                <p className="field-hint">
                  Bisher als Freitext erfasst (nicht mehr aktiv genutzt):{" "}
                  {formData.auftraggeberin}
                </p>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="scale">Scale</label>
              <select
                id="scale"
                name="scale"
                value={formData.scale || ""}
                onChange={handleInputChange}
              >
                <option value="">Bitte wählen...</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
              </select>
            </div>
            <div className="form-group checkbox-group">
              <label htmlFor="public" className="checkbox-label">
                <input
                  type="checkbox"
                  id="public"
                  name="public"
                  checked={formData.public}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      public: e.target.checked,
                    }))
                  }
                />
                <span>Öffentlich</span>
              </label>
            </div>
          </div>
          <div className="form-group checkbox-group">
            <label htmlFor="comingSoon" className="checkbox-label">
              <input
                type="checkbox"
                id="comingSoon"
                name="comingSoon"
                checked={formData.comingSoon}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    comingSoon: e.target.checked,
                  }))
                }
              />
              <span>Coming soon</span>
            </label>
          </div>
          {/* GL-Entscheid: braucht dieses Projekt eine externe Projektwebsite?
              Ist der Schalter aktiv, erscheint auf der internen Projektseite der
              Reiter "Website" (US-WEBP-001). */}
          <div className="form-group checkbox-group">
            <label htmlFor="projektwebsite" className="checkbox-label">
              <input
                type="checkbox"
                id="projektwebsite"
                name="projektwebsite"
                checked={formData.projektwebsite === true}
                disabled={!mayToggleWebsite}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    projektwebsite: e.target.checked,
                  }))
                }
              />
              <span>Externe Projektwebsite</span>
            </label>
            <p className="field-hint">
              {mayToggleWebsite
                ? "Aktiviert den Reiter „Website“ auf der Projektseite. Dem Projekt zugeteilte Team-Mitglieder pflegen dort die Inhalte für die externe Website."
                : "Nur die GL kann eine externe Projektwebsite aktivieren."}
            </p>
          </div>
          <div className="form-group checkbox-group">
            <label htmlFor="color">Farbe</label>
            <input
              type="color"
              id="color"
              name="color"
              value={formData.color}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="importance">Importance (1-20)</label>
            <input
              type="number"
              id="importance"
              name="importance"
              min="1"
              max="20"
              value={formData.importance}
              onChange={handleInputChange}
            />
            {errors.importance && (
              <span className="error-message">{errors.importance}</span>
            )}
          </div>
          {(formData.id || (isEditing && project?.id)) && (
            <div className="form-group">
              <label>Titelbild</label>
              <ImageUploader
                projectId={formData.id || project?.id}
                kategorie="main-picture"
                images={images.mainPicture || []}
                onImagesChange={(newImages) =>
                  handleImagesChange("mainPicture", newImages)
                }
                maxBilder={1}
              />
            </div>
          )}
          {!formData.id && !isEditing && (
            <div className="form-group">
              <p className="info-message">
                Titelbild kann nach dem Erstellen des Projekts hinzugefügt
                werden.
              </p>
            </div>
          )}{" "}
        </div>

        {(formData.id || (isEditing && project?.id)) && (
          <div className="form-group">
            <label>Cover</label>
            <ImageUploader
              projectId={formData.id || project?.id}
              kategorie="cover-image"
              images={images.coverImage || []}
              onImagesChange={(newImages) =>
                handleImagesChange("coverImage", newImages)
              }
              maxBilder={1}
            />
          </div>
        )}

        <div className="form-section">
          <h3>Standort</h3>

          <div className="form-group">
            <label htmlFor="adresse">Adresse</label>
            <input
              type="text"
              id="adresse"
              name="adresse"
              value={formData.adresse}
              onChange={handleInputChange}
              placeholder="z.B. Bahnhofstrasse 123, 4001 Basel"
            />
          </div>

          <div className="form-group">
            <label>Standort auf Karte</label>
            <MapboxLocationPicker
              location={formData.location}
              onLocationChange={handleLocationChange}
              address={formData.adresse}
              height="350px"
              className="project-form-map"
            />
            {errors.location && (
              <span className="error-message">{errors.location}</span>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Tags</h3>

          <div className="tags-container">
            {formData.tags.map((tag, index) => (
              <span
                key={tag.id || index}
                className="tag"
                style={{ backgroundColor: tag.color || "#6c757d" }}
              >
                {tag.name}
                <button
                  type="button"
                  className="tag-remove"
                  onClick={() => removeTag(tag)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          <div className="form-group tag-input-group">
            <input
              type="text"
              value={newTag}
              onChange={(e) => {
                setNewTag(e.target.value);
                setShowTagSuggestions(e.target.value.length > 0);
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag();
                }
              }}
              placeholder="Tag hinzufügen..."
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={addTag}
            >
              Hinzufügen
            </button>

            {showTagSuggestions && getFilteredTags().length > 0 && (
              <div className="tag-suggestions">
                {getFilteredTags().map((tag) => (
                  <div
                    key={tag.id}
                    className="tag-suggestion"
                    onClick={() => handleTagSelect(tag)}
                  >
                    <span
                      className="tag-color"
                      style={{ backgroundColor: tag.color }}
                    ></span>
                    {tag.name} ({tag.usageCount})
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="popular-tags">
            <h4>Beliebte Tags</h4>
            <div className="tags-container">
              {popularTags.length > 0 ? (
                popularTags.map((tag) => {
                  const isSelected = formData.tags.some(
                    (selectedTag) => selectedTag.id === tag.id
                  );
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      className={`tag-button ${isSelected ? "selected" : ""}`}
                      style={{
                        backgroundColor: isSelected ? tag.color : "transparent",
                        borderColor: tag.color,
                        color: isSelected ? "white" : tag.color,
                      }}
                      onClick={() => addPredefinedTag(tag)}
                      disabled={isSelected}
                    >
                      {tag.name} ({tag.usageCount})
                    </button>
                  );
                })
              ) : (
                <p className="no-popular-tags">
                  Noch keine beliebten Tags vorhanden
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Beteiligte Personen</h3>

          <div className="form-group person-input-group">
            <div className="autocomplete-container">
              <input
                type="text"
                value={personSearch}
                onChange={(e) => {
                  setPersonSearch(e.target.value);
                  setShowPersonSuggestions(e.target.value.length > 0);
                }}
                onFocus={() => setShowPersonSuggestions(true)}
                onBlur={() =>
                  setTimeout(() => setShowPersonSuggestions(false), 200)
                }
                placeholder="Person suchen..."
              />
              {showPersonSuggestions && getFilteredPersons().length > 0 && (
                <div className="suggestions-dropdown">
                  {getFilteredPersons().map((person) => (
                    <div
                      key={person.id}
                      className="suggestion-item"
                      onClick={() => handlePersonSelect(person)}
                    >
                      <strong>
                        {person.firstName} {person.lastName}
                      </strong>
                      {person.email && (
                        <span className="suggestion-email">{person.email}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="persons-list">
            {formData.persons && formData.persons.length > 0 ? (
              formData.persons.map((person, index) => (
                <div key={person.id} className="person-item-compact">
                  <span className="person-name">
                    {person.firstName} {person.lastName}
                  </span>
                  <span className="person-email">{person.email}</span>
                  <div className="person-phases-dropdown">
                    <select
                      multiple
                      value={person.phases || []}
                      onChange={(e) => handlePersonPhasesChange(person.id, e)}
                      className="phases-select-compact"
                      size="1"
                    >
                      <option value="11">
                        11 - Bedürfnisformulierung, Lösungsstrategien
                      </option>
                      <option value="21">
                        21 - Definition des Bauvorhabens, Machbarkeitsstudie
                      </option>
                      <option value="22">22 - Auswahlverfahren</option>
                      <option value="31">31 - Vorprojekt</option>
                      <option value="32">32 - Bauprojekt</option>
                      <option value="33">
                        33 - Bewilligungsverfahren / Auflageprojekt
                      </option>
                      <option value="41">
                        41 - Ausschreibung, Offertvergleich, Vergabeantrag
                      </option>
                      <option value="51">51 - Ausführungsprojekt</option>
                      <option value="52">52 - Ausführung</option>
                      <option value="53">53 - Inbetriebnahme, Abschluss</option>
                      <option value="61">61 - Betrieb</option>
                      <option value="62">
                        62 - Überwachung / Überprüfung / Wartung
                      </option>
                      <option value="63">63 - Instandhaltung</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    className="btn-remove-person-compact"
                    onClick={() => removePerson(person.id)}
                  >
                    Entfernen
                  </button>
                </div>
              ))
            ) : (
              <p className="no-persons">Noch keine Personen hinzugefügt</p>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Beschreibungen</h3>

          <div className="form-group">
            <label htmlFor="beschreibung">Projektbeschreibung</label>
            <textarea
              id="beschreibung"
              name="beschreibung"
              value={formData.beschreibung}
              onChange={handleInputChange}
              rows="4"
              placeholder="Detaillierte Beschreibung des Projekts..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="websiteUrl">Website-URL</label>
            <input
              type="url"
              id="websiteUrl"
              name="websiteUrl"
              value={formData.websiteUrl}
              onChange={handleInputChange}
              placeholder="https://example.com"
            />
          </div>
        </div>

        {(formData.id || (isEditing && project?.id)) && (
          <div className="form-section image-uploader-section">
            <h3>Bilder</h3>

            <ImageUploader
              projectId={formData.id || project?.id}
              kategorie="allgemein"
              images={images.allgemein}
              onImagesChange={(newImages) =>
                handleImagesChange("allgemein", newImages)
              }
              maxBilder={10}
            />

            <ImageUploader
              projectId={formData.id || project?.id}
              kategorie="plaene"
              images={images.plaene}
              onImagesChange={(newImages) =>
                handleImagesChange("plaene", newImages)
              }
              maxBilder={15}
            />

            <ImageUploader
              projectId={formData.id || project?.id}
              kategorie="prozess"
              images={images.prozess}
              onImagesChange={(newImages) =>
                handleImagesChange("prozess", newImages)
              }
              maxBilder={20}
            />
          </div>
        )}

        {!formData.id && !isEditing && (
          <div className="form-section">
            <div className="info-message">
              <p>
                Bilder können nach dem Erstellen des Projekts hinzugefügt
                werden.
              </p>
            </div>
          </div>
        )}

        {(formData.id || (isEditing && project?.id)) && (
          <div className="form-section">
            <MediaTable
              projectId={formData.id || project?.id}
              media={media}
              onMediaChange={setMedia}
            />
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Abbrechen
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading
              ? "Speichern..."
              : isEditing
              ? "Aktualisieren"
              : "Erstellen"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;
